import React, { Children, cloneElement, createContext, isValidElement, useMemo, useState } from 'react'
import PropTypes from 'prop-types'

/**
 * Internal context shared by WingsAccordion and WingsDropdown.
 *
 * @type {React.Context<{activeItemKey: string|null, setActiveItemKey: Function}|null>}
 */
export const WingsAccordionContext = createContext(null)

/**
 * WingsAccordion Component
 *
 * Groups WingsDropdown children so only one item can be open at a time.
 *
 * @component
 * @example
 * <WingsAccordion>
 *   <WingsDropdown title="One">...</WingsDropdown>
 *   <WingsDropdown title="Two">...</WingsDropdown>
 * </WingsAccordion>
 */
const WingsAccordion = ({ children, className = '', defaultOpenKey = null, ...props }) => {
  const childArray = Children.toArray(children)
  const initialOpenKey = defaultOpenKey ?? childArray.find((child) => isValidElement(child) && child.props.defaultOpen)?.key ?? null
  const [activeItemKey, setActiveItemKey] = useState(initialOpenKey)

  const accordionChildren = useMemo(() => (
    childArray.map((child, index) => {
      if (!isValidElement(child)) return child

      return cloneElement(child, {
        accordionItemKey: child.props.accordionItemKey ?? child.key ?? `${index}`,
      })
    })
  ), [childArray])

  const accordionClasses = ['wings-accordion', className].filter(Boolean).join(' ')

  return (
    <WingsAccordionContext.Provider value={{ activeItemKey, setActiveItemKey }}>
      <div className={accordionClasses} {...props}>
        {accordionChildren}
      </div>
    </WingsAccordionContext.Provider>
  )
}

WingsAccordion.propTypes = {
  /** Accordion items */
  children: PropTypes.node.isRequired,

  /** Optional initial open item key */
  defaultOpenKey: PropTypes.string,

  /** Additional CSS classes */
  className: PropTypes.string,
}

export default WingsAccordion